import clsx from 'clsx';
import type { BreadcrumbProps } from './types';
import { BreadcrumbSegment } from './partials/BreadcrumbSegment';
import styles from './breadcrumb.module.css';

/**
 * Breadcrumb navigation component for displaying hierarchical page paths
 *
 * This component renders a breadcrumb trail showing the navigation hierarchy from
 * the home page to the current page. It includes:
 * - Accessible navigation with proper ARIA attributes
 * - Link masking for intermediate breadcrumb items
 * - Non-clickable current page item
 * - Responsive design for mobile and desktop
 * - Mobile collapse/expand functionality for long breadcrumbs (CSS-only, no JavaScript)
 * - Schema.org BreadcrumbList structured data for SEO
 *
 * The component automatically:
 * - Marks the last item with aria-current="page"
 * - Renders links for all items except the current page
 * - Hides when there's only one item (home page only)
 * - Collapses to show "... › [second-to-last] › [last]" on mobile
 * - Renders JSON-LD schema if provided
 *
 * @param props - Breadcrumb component properties
 * @param props.items - Array of breadcrumb items to render
 * @param props.className - Optional CSS class for custom styling
 * @param props.ariaLabel - Aria label for accessibility (defaults to "Breadcrumb")
 * @param props.schema - Optional schema.org BreadcrumbList JSON-LD data
 *
 * @returns Breadcrumb navigation or null if insufficient items
 *
 * @example
 * ```tsx
 * <Breadcrumb
 *   items={[
 *     { title: 'Home', url: '/', isCurrentPage: false },
 *     { title: 'Products', url: '/products', isCurrentPage: false },
 *     { title: 'Photo Book', url: '/products/photo-book', isCurrentPage: true }
 *   ]}
 *   schema={breadcrumbSchema}
 * />
 * ```
 */
export function Breadcrumb({ items, className, ariaLabel = 'Breadcrumb', schema }: BreadcrumbProps) {
  // Don't render if there's only one item (home page only) or no items
  if (!items || items.length <= 1) {
    return null;
  }

  // First item is collapsed on Mobile
  const shouldBeCollapsed = items.length > 2;

  return (
    <>
      <section className="breadcrumb-container center--content">
        <nav className={clsx(styles.breadcrumb, className)} aria-label={ariaLabel}>
          {shouldBeCollapsed && (
            <input
              type="checkbox"
              id="breadcrumb-toggle"
              className={styles.breadcrumb__toggle}
              aria-label="Toggle breadcrumb expansion"
            />
          )}
          <ol className={styles.breadcrumb__items}>
            {shouldBeCollapsed && (
              <li className={`${styles.breadcrumb__item} text--overline`}>
                <label
                  htmlFor="breadcrumb-toggle"
                  className={styles.breadcrumb__expand}
                  aria-label="Show full breadcrumb"
                >
                  ...
                </label>
              </li>
            )}
            {items.map((item, index) => {
              const isFirst = index === 0;
              const isLast = index === items.length - 1;
              const isCollapsed = shouldBeCollapsed && isFirst;

              return (
                <BreadcrumbSegment key={`${item.url}-${index}`} item={item} isLast={isLast} isCollapsed={isCollapsed} />
              );
            })}
          </ol>
        </nav>
      </section>
      {schema && (
        <script
          id="breadcrumb-schema"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      )}
    </>
  );
}
